export type SessionStatus = 'active' | 'time_over' | 'lives_over' | 'completed'

export type ChallengePhase = 'READY' | 'LOCKED_WRONG' | 'CORRECT'

export interface FlowState {
  steps: Step[]
  currentIndex: number
  status: SessionStatus

  lives: number
  maxLives: number

  timeLeft: number
  canVerify: boolean
  canContinue: boolean

  phase: ChallengePhase
  feedback?: { kind: 'error' | 'success' | 'info'; message: string } | null
}

export enum STEP_KIND {
  INITIAL = 'INITIAL',
  INTRO = 'INTRO',
  CHALLENGE = 'CHALLENGE',
  SUMMARY = 'SUMMARY'
}

export type ChallengeKind =
  | 'word_hunt'
  | 'this_or_that'
  | 'video'
  | 'memory'
  | 'question'
  | 'slide'
  | 'stories'
  | 'file'
  | 'iframe'
  | 'puzzle'
  | string

export const MINIMUM_ADVANCE_CONTENT_KINDS = [
  'stories',
  'slide',
  'video',
  'iframe',
  'file'
] as const

export type MinimumAdvanceContentKind =
  (typeof MINIMUM_ADVANCE_CONTENT_KINDS)[number]

export type Challenge = {
  id: number
  uid: string
  name: string
  kind: ChallengeKind
  position: number
  intro?: string | null
  meta?: { slides?: unknown[]; [k: string]: unknown }
  time_limit?: number
  minimum_time_to_advance_in_seconds?: number | null
  items?: Record<string, unknown>
}

export type StepBase = {
  id: string
  kind: STEP_KIND
  position: number
  challengeId?: number
  time_limit: number | null
  minimumTimeToAdvanceInSeconds?: number | null
}

export type InitialStep = StepBase & {
  kind: STEP_KIND.INITIAL
  isInitial: true
}

export type IntroStep = StepBase & {
  kind: STEP_KIND.INTRO
  isIntro: true
  challengeId: number
  text?: string | null
}

export type ChallengeStep = StepBase & {
  kind: STEP_KIND.CHALLENGE
  challengeId: number
  challengeKind: ChallengeKind
  mergedStoryChallengeIds?: number[]
}

export type SlideStep = ChallengeStep & {
  kind: STEP_KIND.CHALLENGE
  isSlide: true
  slideIndex: number
  isFirstSlide: boolean
  isLastSlide: boolean
}

export type SummaryStep = StepBase & {
  kind: STEP_KIND.SUMMARY
  result?: 'success' | 'fail'
}

export type Step =
  | InitialStep
  | IntroStep
  | SlideStep
  | ChallengeStep
  | SummaryStep
